import { useCallback, useEffect, useState } from "react";
import { Laptop, Smartphone, Tablet, MonitorSmartphone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import dayjs from "dayjs";
import { toast } from "sonner";

const ICON = { mobile: Smartphone, tablet: Tablet, desktop: Laptop };
const when = (d) => (d ? dayjs(d).format("D MMM YYYY, HH:mm") : "—");

const Row = ({ s, onRevoke, busy }) => {
  const Icon = ICON[s.kind] || MonitorSmartphone;
  const name = [s.browser, s.os && `su ${s.os}`].filter(Boolean).join(" ") || "Dispositivo sconosciuto";
  return (
    <li className="flex items-center gap-3 py-3 border-b last:border-0" data-testid={`session-row-${s.id}`}>
      <span className="h-9 w-9 shrink-0 rounded-full bg-slate-100 grid place-items-center"><Icon className="h-4 w-4 text-[#1E3A8A]" /></span>
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{name} {s.current && <span className="ml-1 text-xs rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5" data-testid="session-current-badge">Questo dispositivo</span>}</p>
        <p className="text-xs text-slate-500 truncate">
          {s.method === "google" ? "Google" : s.method === "password" ? "Email e password" : "Accesso"} · ultimo uso {when(s.last_seen)}{s.ip ? ` · IP ${s.ip}` : ""}
        </p>
      </div>
      {!s.current && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onRevoke(s.id)} className="rounded-full shrink-0" data-testid={`session-revoke-${s.id}`}>Disconnetti</Button>
      )}
    </li>
  );
};

export const DeviceSessions = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/auth/sessions").then(({ data }) => setList(data)).catch(() => setList([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const revoke = (id) => run(() => api.delete(`/auth/sessions/${id}`), "Dispositivo disconnesso");
  const revokeOthers = () => run(() => api.post("/auth/sessions/revoke-others"), "Tutti gli altri dispositivi sono stati disconnessi");
  const others = (list || []).filter((s) => !s.current).length;

  return (
    <div className="rounded-3xl border bg-white p-6 min-w-0" data-testid="sessions-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium flex items-center gap-2"><MonitorSmartphone className="h-4 w-4 text-[#1E3A8A]" /> Dispositivi collegati</h3>
        {others > 0 && <Button size="sm" variant="ghost" disabled={busy} onClick={revokeOthers} className="rounded-full text-red-600" data-testid="sessions-revoke-others">Disconnetti tutti gli altri</Button>}
      </div>
      {!list ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-400" /> : (
        <ul className="mt-2 text-sm" data-testid="sessions-list">{list.map((s) => <Row key={s.id} s={s} onRevoke={revoke} busy={busy} />)}</ul>
      )}
      <p className="mt-3 text-xs text-slate-500">Non riconosci un dispositivo? Disconnettilo e, se usi una password, cambiala.</p>
    </div>
  );
};
